import {
  FISHYMAP_POINT_ICON_SCALE_MAX,
  FISHYMAP_POINT_ICON_SCALE_MIN,
} from "./map-bridge-contract.js";
import {
  layerHighlightRecordsEqual,
  normalizeLayerHighlightRecord,
} from "./map-layer-highlight-style.js";
import { DEFAULT_ENABLED_LAYER_IDS } from "./map-signal-contract.js";
import {
  DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  TRADE_NPC_CLASS_DEFINITIONS,
  TRADE_NPC_LAYER_ID,
  normalizeVisibleTradeNpcClasses,
} from "./map-trade-npc-contract.js";

const FIXED_GROUND_LAYER_IDS = new Set();

function hasOwnKey(object, key) {
  return !!object && Object.prototype.hasOwnProperty.call(object, key);
}

function isPlainObject(value) {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function normalizeLayerHighlightSummary(value) {
  if (!isPlainObject(value)) {
    return null;
  }
  const defaultHighlight = normalizeLayerHighlightRecord(value.default);
  if (!defaultHighlight) {
    return null;
  }
  return {
    current: normalizeLayerHighlightRecord(value.current) || defaultHighlight,
    default: defaultHighlight,
  };
}

export function clampPointIconScale(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return FISHYMAP_POINT_ICON_SCALE_MIN;
  }
  return Math.min(FISHYMAP_POINT_ICON_SCALE_MAX, Math.max(FISHYMAP_POINT_ICON_SCALE_MIN, number));
}

export function pointIconScaleValue(scale) {
  return String(Math.round(clampPointIconScale(scale) * 100) / 100);
}

export function pointIconScaleLabel(scale) {
  return `${Math.round(clampPointIconScale(scale) * 100)}%`;
}

export function clampLayerOpacity(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) {
    return 1;
  }
  return Math.min(1, Math.max(0, number));
}

export function layerOpacityValue(opacity) {
  return String(Math.round(clampLayerOpacity(opacity) * 100) / 100);
}

export function layerOpacityLabel(opacity) {
  return `${Math.round(clampLayerOpacity(opacity) * 100)}%`;
}

export function isFixedGroundLayer(layerId) {
  return FIXED_GROUND_LAYER_IDS.has(String(layerId || "").trim());
}

export function isLayerClipMaskSource(layer) {
  const kind = String(layer?.kind || "").trim();
  return kind === "field" || kind === "vector-geojson";
}

export function resolveLayerEntries(stateBundle) {
  const layers = Array.isArray(stateBundle.state?.catalog?.layers)
    ? stateBundle.state.catalog.layers.slice()
    : [];
  const orderedIds = Array.isArray(stateBundle.inputState?.filters?.layerIdsOrdered)
    ? stateBundle.inputState.filters.layerIdsOrdered
    : [];
  const visibleOverride = Array.isArray(stateBundle.inputState?.filters?.layerIdsVisible)
    ? new Set(stateBundle.inputState.filters.layerIdsVisible)
    : null;
  const inputOpacityOverride = isPlainObject(stateBundle.inputState?.filters?.layerOpacities)
    ? stateBundle.inputState.filters.layerOpacities
    : null;
  const inputClipMaskOverride = isPlainObject(stateBundle.inputState?.filters?.layerClipMasks)
    ? stateBundle.inputState.filters.layerClipMasks
    : null;
  const inputWaypointConnectionsOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerWaypointConnectionsVisible,
  )
    ? stateBundle.inputState.filters.layerWaypointConnectionsVisible
    : null;
  const inputWaypointLabelsOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerWaypointLabelsVisible,
  )
    ? stateBundle.inputState.filters.layerWaypointLabelsVisible
    : null;
  const inputPointIconsOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerPointIconsVisible,
  )
    ? stateBundle.inputState.filters.layerPointIconsVisible
    : null;
  const inputPointIconScaleOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerPointIconScales,
  )
    ? stateBundle.inputState.filters.layerPointIconScales
    : null;
  const inputPointClusteringOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerPointClusteringEnabled,
  )
    ? stateBundle.inputState.filters.layerPointClusteringEnabled
    : null;
  const inputHighlightOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerHighlights,
  )
    ? stateBundle.inputState.filters.layerHighlights
    : null;
  const inputConfidenceShowcaseOverride = isPlainObject(
    stateBundle.inputState?.filters?.layerConfidenceShowcase,
  )
    ? stateBundle.inputState.filters.layerConfidenceShowcase
    : null;
  const visibleTradeNpcClasses = new Set(normalizeVisibleTradeNpcClasses(
    stateBundle.inputState?.filters?.tradeNpcClassesVisible
      ?? DEFAULT_VISIBLE_TRADE_NPC_CLASSES,
  ));
  const byId = new Map(layers.map((layer) => [layer.layerId, layer]));
  const seen = new Set();
  const movable = [];
  const pinned = [];

  const pushLayer = (layer) => {
    if (!layer || seen.has(layer.layerId)) {
      return;
    }
    seen.add(layer.layerId);
    const visible = visibleOverride ? visibleOverride.has(layer.layerId) : Boolean(layer.visible);
    const opacityDefault = clampLayerOpacity(layer.opacityDefault ?? 1);
    let opacity = clampLayerOpacity(layer.opacity);
    if (inputOpacityOverride) {
      opacity = hasOwnKey(inputOpacityOverride, layer.layerId)
        ? clampLayerOpacity(inputOpacityOverride[layer.layerId])
        : opacityDefault;
    }
    let clipMaskLayerId = null;
    if (inputClipMaskOverride) {
      clipMaskLayerId = hasOwnKey(inputClipMaskOverride, layer.layerId)
        ? String(inputClipMaskOverride[layer.layerId] || "").trim() || null
        : null;
    }
    const supportsWaypointConnections = layer.supportsWaypointConnections === true;
    const waypointConnectionsDefault = supportsWaypointConnections
      ? layer.waypointConnectionsDefault !== false
      : false;
    let waypointConnectionsVisible = supportsWaypointConnections
      ? layer.waypointConnectionsVisible !== false
      : false;
    if (supportsWaypointConnections && inputWaypointConnectionsOverride) {
      waypointConnectionsVisible = hasOwnKey(inputWaypointConnectionsOverride, layer.layerId)
        ? inputWaypointConnectionsOverride[layer.layerId] !== false
        : waypointConnectionsDefault;
    }
    const supportsWaypointLabels = layer.supportsWaypointLabels === true;
    const waypointLabelsDefault = supportsWaypointLabels
      ? layer.waypointLabelsDefault !== false
      : false;
    let waypointLabelsVisible = supportsWaypointLabels
      ? layer.waypointLabelsVisible !== false
      : false;
    if (supportsWaypointLabels && inputWaypointLabelsOverride) {
      waypointLabelsVisible = hasOwnKey(inputWaypointLabelsOverride, layer.layerId)
        ? inputWaypointLabelsOverride[layer.layerId] !== false
        : waypointLabelsDefault;
    }
    const supportsPointIcons = layer.supportsPointIcons === true;
    const pointIconsDefault = supportsPointIcons ? layer.pointIconsDefault !== false : false;
    let pointIconsVisible = supportsPointIcons ? layer.pointIconsVisible !== false : false;
    if (supportsPointIcons && inputPointIconsOverride) {
      pointIconsVisible = hasOwnKey(inputPointIconsOverride, layer.layerId)
        ? inputPointIconsOverride[layer.layerId] !== false
        : pointIconsDefault;
    }
    const pointIconScaleDefault = supportsPointIcons
      ? clampPointIconScale(layer.pointIconScaleDefault ?? FISHYMAP_POINT_ICON_SCALE_MIN)
      : FISHYMAP_POINT_ICON_SCALE_MIN;
    let pointIconScale = supportsPointIcons
      ? clampPointIconScale(layer.pointIconScale ?? pointIconScaleDefault)
      : FISHYMAP_POINT_ICON_SCALE_MIN;
    if (supportsPointIcons && inputPointIconScaleOverride) {
      pointIconScale = hasOwnKey(inputPointIconScaleOverride, layer.layerId)
        ? clampPointIconScale(inputPointIconScaleOverride[layer.layerId])
        : pointIconScaleDefault;
    }
    const supportsPointClustering = layer.supportsPointClustering === true;
    const pointClusteringDefault = supportsPointClustering
      ? layer.pointClusteringDefault !== false
      : false;
    let pointClusteringEnabled = supportsPointClustering
      ? layer.pointClusteringEnabled !== false
      : false;
    if (supportsPointClustering && inputPointClusteringOverride) {
      pointClusteringEnabled = hasOwnKey(inputPointClusteringOverride, layer.layerId)
        ? inputPointClusteringOverride[layer.layerId] !== false
        : pointClusteringDefault;
    }
    const highlightSummary = normalizeLayerHighlightSummary(layer.highlight);
    if (highlightSummary && inputHighlightOverride) {
      highlightSummary.current = hasOwnKey(inputHighlightOverride, layer.layerId)
        ? normalizeLayerHighlightRecord(inputHighlightOverride[layer.layerId]) || highlightSummary.default
        : highlightSummary.default;
    }
    const confidenceSummary = isPlainObject(layer.confidenceShowcase)
      ? {
          current: layer.confidenceShowcase.current === true,
          default: layer.confidenceShowcase.default === true,
        }
      : null;
    const supportsConfidenceShowcase = confidenceSummary != null;
    const supportsCoordinateLineage = layer.supportsCoordinateLineage === true;
    let confidenceShowcase = confidenceSummary?.current === true;
    if (supportsConfidenceShowcase && inputConfidenceShowcaseOverride) {
      confidenceShowcase = hasOwnKey(inputConfidenceShowcaseOverride, layer.layerId)
        ? inputConfidenceShowcaseOverride[layer.layerId] === true
        : confidenceSummary.default;
    }
    const entry = {
      ...layer,
      visible,
      opacity,
      opacityDefault,
      clipMaskLayerId,
      supportsWaypointConnections,
      waypointConnectionsVisible,
      waypointConnectionsDefault,
      supportsWaypointLabels,
      waypointLabelsVisible,
      waypointLabelsDefault,
      supportsPointIcons,
      pointIconsVisible,
      pointIconsDefault,
      pointIconScale,
      pointIconScaleDefault,
      supportsPointClustering,
      pointClusteringEnabled,
      pointClusteringDefault,
      supportsConfidenceShowcase,
      confidenceShowcase,
      confidenceShowcaseDefault: confidenceSummary?.default === true,
      supportsCoordinateLineage,
      locked: isFixedGroundLayer(layer.layerId),
    };
    if (layer.layerId === TRADE_NPC_LAYER_ID) {
      entry.tradeNpcClasses = TRADE_NPC_CLASS_DEFINITIONS.map((definition) => ({
        ...definition,
        visible: visibleTradeNpcClasses.has(definition.id),
      }));
    }
    if (highlightSummary) {
      entry.highlight = highlightSummary;
    } else {
      delete entry.highlight;
    }
    if (entry.locked) {
      pinned.push(entry);
    } else {
      movable.push(entry);
    }
  };

  for (const layerId of orderedIds) {
    pushLayer(byId.get(layerId));
  }

  const fallback = layers.slice().sort((left, right) => {
    const leftOrder = Number.isFinite(left?.displayOrder) ? left.displayOrder : 0;
    const rightOrder = Number.isFinite(right?.displayOrder) ? right.displayOrder : 0;
    return rightOrder - leftOrder || String(left?.layerId || "").localeCompare(String(right?.layerId || ""));
  });
  for (const layer of fallback) {
    pushLayer(layer);
  }

  return movable.concat(pinned);
}

export function resolveVisibleLayerIds(stateBundle) {
  return resolveLayerEntries(stateBundle)
    .filter((layer) => layer.visible)
    .map((layer) => layer.layerId);
}

export function moveLayerIdBefore(entries, draggedLayerId, targetLayerId, position) {
  const movableIds = entries.filter((layer) => !layer.locked).map((layer) => layer.layerId);
  const fromIndex = movableIds.indexOf(draggedLayerId);
  const targetIndex = movableIds.indexOf(targetLayerId);
  if (fromIndex < 0 || targetIndex < 0) {
    return movableIds;
  }
  const [dragged] = movableIds.splice(fromIndex, 1);
  const insertIndex = position === "after"
    ? targetIndex + (fromIndex < targetIndex ? 0 : 1)
    : targetIndex + (fromIndex < targetIndex ? -1 : 0);
  movableIds.splice(Math.max(0, insertIndex), 0, dragged);
  const pinnedIds = entries.filter((layer) => layer.locked).map((layer) => layer.layerId);
  return movableIds.concat(pinnedIds);
}

function resolveTopClipMaskLayerId(clipMasks, layerId) {
  const normalizedLayerId = String(layerId || "").trim();
  if (!normalizedLayerId) {
    return "";
  }
  const seen = new Set([normalizedLayerId]);
  let cursor = String(clipMasks[normalizedLayerId] || "").trim();
  while (cursor) {
    if (seen.has(cursor) || cursor === normalizedLayerId || isFixedGroundLayer(cursor)) {
      return "";
    }
    seen.add(cursor);
    const nextMaskLayerId = String(clipMasks[cursor] || "").trim();
    if (!nextMaskLayerId || nextMaskLayerId === cursor || isFixedGroundLayer(nextMaskLayerId)) {
      return cursor;
    }
    cursor = nextMaskLayerId;
  }
  return "";
}

export function flattenLayerClipMasks(clipMasks) {
  const flattened = {};
  for (const [layerId, maskLayerId] of Object.entries(clipMasks || {})) {
    const normalizedLayerId = String(layerId || "").trim();
    const normalizedMaskLayerId = String(maskLayerId || "").trim();
    if (
      !normalizedLayerId ||
      !normalizedMaskLayerId ||
      normalizedLayerId === normalizedMaskLayerId ||
      isFixedGroundLayer(normalizedLayerId) ||
      isFixedGroundLayer(normalizedMaskLayerId)
    ) {
      continue;
    }
    const topMaskLayerId = resolveTopClipMaskLayerId(
      { ...clipMasks, [normalizedLayerId]: normalizedMaskLayerId },
      normalizedLayerId,
    );
    if (!topMaskLayerId || topMaskLayerId === normalizedLayerId) {
      continue;
    }
    flattened[normalizedLayerId] = topMaskLayerId;
  }
  return flattened;
}

function collectAttachedLayerIds(clipMasks, rootLayerId) {
  const normalizedRootLayerId = String(rootLayerId || "").trim();
  if (!normalizedRootLayerId) {
    return new Set();
  }
  const attachedLayerIds = new Set([normalizedRootLayerId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const [layerId, maskLayerId] of Object.entries(clipMasks || {})) {
      const normalizedLayerId = String(layerId || "").trim();
      const normalizedMaskLayerId = String(maskLayerId || "").trim();
      if (
        !normalizedLayerId ||
        !normalizedMaskLayerId ||
        attachedLayerIds.has(normalizedLayerId) ||
        !attachedLayerIds.has(normalizedMaskLayerId)
      ) {
        continue;
      }
      attachedLayerIds.add(normalizedLayerId);
      changed = true;
    }
  }
  return attachedLayerIds;
}

export function buildLayerOpacityPatch(stateBundle, targetLayerId, opacity) {
  const nextOpacities = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (layer.locked) {
      continue;
    }
    const effectiveOpacity =
      layer.layerId === targetLayerId ? clampLayerOpacity(opacity) : clampLayerOpacity(layer.opacity);
    if (Math.abs(effectiveOpacity - clampLayerOpacity(layer.opacityDefault)) <= 0.0001) {
      continue;
    }
    nextOpacities[layer.layerId] = effectiveOpacity;
  }
  return nextOpacities;
}

export function buildLayerClipMaskPatch(stateBundle, targetLayerId, maskLayerId) {
  const entries = resolveLayerEntries(stateBundle);
  const layerById = new Map(entries.map((layer) => [layer.layerId, layer]));
  const nextClipMasks = {};
  for (const layer of entries) {
    if (layer.locked) {
      continue;
    }
    const currentMaskLayerId = String(layer.clipMaskLayerId || "").trim();
    if (
      !currentMaskLayerId ||
      currentMaskLayerId === layer.layerId ||
      isFixedGroundLayer(currentMaskLayerId) ||
      !isLayerClipMaskSource(layerById.get(currentMaskLayerId))
    ) {
      continue;
    }
    nextClipMasks[layer.layerId] = currentMaskLayerId;
  }
  const normalizedTargetLayerId = String(targetLayerId || "").trim();
  const normalizedMaskLayerId = String(maskLayerId || "").trim();
  if (!normalizedTargetLayerId || isFixedGroundLayer(normalizedTargetLayerId)) {
    return flattenLayerClipMasks(nextClipMasks);
  }
  if (
    !normalizedMaskLayerId ||
    isFixedGroundLayer(normalizedMaskLayerId) ||
    !isLayerClipMaskSource(layerById.get(normalizedMaskLayerId))
  ) {
    delete nextClipMasks[normalizedTargetLayerId];
    return flattenLayerClipMasks(nextClipMasks);
  }

  const draggedSubtree = collectAttachedLayerIds(nextClipMasks, normalizedTargetLayerId);
  const targetSubtree = collectAttachedLayerIds(nextClipMasks, normalizedMaskLayerId);

  delete nextClipMasks[normalizedMaskLayerId];
  for (const layerId of targetSubtree) {
    if (layerId === normalizedMaskLayerId) {
      continue;
    }
    nextClipMasks[layerId] = normalizedMaskLayerId;
  }
  for (const layerId of draggedSubtree) {
    if (layerId === normalizedMaskLayerId) {
      continue;
    }
    nextClipMasks[layerId] = normalizedMaskLayerId;
  }
  return flattenLayerClipMasks(nextClipMasks);
}

export function buildLayerWaypointConnectionsPatch(stateBundle, targetLayerId, visible) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsWaypointConnections) {
      continue;
    }
    const effectiveVisible =
      layer.layerId === targetLayerId ? visible !== false : layer.waypointConnectionsVisible !== false;
    if (effectiveVisible === (layer.waypointConnectionsDefault !== false)) {
      continue;
    }
    next[layer.layerId] = effectiveVisible;
  }
  return next;
}

export function buildLayerWaypointLabelsPatch(stateBundle, targetLayerId, visible) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsWaypointLabels) {
      continue;
    }
    const effectiveVisible =
      layer.layerId === targetLayerId ? visible !== false : layer.waypointLabelsVisible !== false;
    if (effectiveVisible === (layer.waypointLabelsDefault !== false)) {
      continue;
    }
    next[layer.layerId] = effectiveVisible;
  }
  return next;
}

export function buildLayerPointIconsPatch(stateBundle, targetLayerId, visible) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsPointIcons) {
      continue;
    }
    const effectiveVisible =
      layer.layerId === targetLayerId ? visible !== false : layer.pointIconsVisible !== false;
    if (effectiveVisible === (layer.pointIconsDefault !== false)) {
      continue;
    }
    next[layer.layerId] = effectiveVisible;
  }
  return next;
}

export function buildLayerConfidenceShowcasePatch(stateBundle, targetLayerId, enabled) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsConfidenceShowcase) {
      continue;
    }
    const effectiveEnabled =
      layer.layerId === targetLayerId ? enabled === true : layer.confidenceShowcase === true;
    if (effectiveEnabled === (layer.confidenceShowcaseDefault === true)) {
      continue;
    }
    next[layer.layerId] = effectiveEnabled;
  }
  return next;
}

export function buildLayerPointIconScalePatch(stateBundle, targetLayerId, scale) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsPointIcons) {
      continue;
    }
    const effectiveScale =
      layer.layerId === targetLayerId
        ? clampPointIconScale(scale)
        : clampPointIconScale(layer.pointIconScale);
    if (Math.abs(effectiveScale - clampPointIconScale(layer.pointIconScaleDefault)) <= 0.0001) {
      continue;
    }
    next[layer.layerId] = effectiveScale;
  }
  return next;
}

export function buildLayerPointClusteringPatch(stateBundle, targetLayerId, enabled) {
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    if (!layer.supportsPointClustering) {
      continue;
    }
    const effectiveEnabled =
      layer.layerId === targetLayerId
        ? enabled !== false
        : layer.pointClusteringEnabled !== false;
    if (effectiveEnabled === (layer.pointClusteringDefault !== false)) {
      continue;
    }
    next[layer.layerId] = effectiveEnabled;
  }
  return next;
}

export function buildLayerHighlightsPatch(stateBundle, targetLayerId, changes = {}) {
  const normalizedTargetLayerId = String(targetLayerId || "").trim();
  const next = {};
  for (const layer of resolveLayerEntries(stateBundle)) {
    const summary = normalizeLayerHighlightSummary(layer.highlight);
    if (!summary) {
      continue;
    }
    const effective =
      layer.layerId === normalizedTargetLayerId
        ? normalizeLayerHighlightRecord({
            ...summary.current,
            ...(isPlainObject(changes) ? changes : {}),
          }) || summary.current
        : summary.current;
    if (layerHighlightRecordsEqual(effective, summary.default)) {
      continue;
    }
    next[layer.layerId] = effective;
  }
  return next;
}
